/* eslint-disable sonarjs/no-duplicate-string */
import HttpRequest from '@Services/RequestFactory';

/**
 * @ngdoc service
 * @type constant
 * @name RequestUrls
 * @description
 * Keeps URLS for HTTP Requests
 * Constant for tagging http requests as real or fake (property 'through').
 *   true = real - Data are requested from real server
 *   false = fake - Data are requested from fake backend
 * If 'through' = false, this have to have 'provider' property, with name of provider, who can returns mock data
 * @return {Object} that holds RequestUrls
 */

const CONDITION_NAVIGATOR_API_URL = '/api/conditionNavigator';

const RequestUrls = {
  rcsSupplierInfo: {
    url: HttpRequest.getUrlWithPrefix('/results/cit/crn?databaseId={databaseId}&crn={crn}'),
    urlRegex: /\/results\/cit\/crn/,
    nonRepeatable: false,
    method: 'GET',
  },
  changePassword: {
    url: HttpRequest.getUrlWithPrefix('/userprofile/password'),
    urlRegex: /\/userprofile\/password/,
    nonRepeatable: true,
    method: 'POST',
  },
  changeEmail: {
    url: HttpRequest.getUrlWithPrefix('/userprofile/email'),
    nonRepeatable: true,
    method: 'POST',
  },
  resetPassword: {
    url: HttpRequest.getUrlWithPrefix('/public/reset/password?address={address}', 'authPrefix'),
    method: 'GET',
    nonRepeatable: true,
  },
  resetPasswordWithToken: {
    url: HttpRequest.getUrlWithPrefix('/public/reset/password', 'authPrefix'),
    method: 'POST',
    nonRepeatable: true,
  },
  resetPasswordValidate: {
    url: HttpRequest.getUrlWithPrefix('/public/reset/password/validate/{token}', 'authPrefix'),
    method: 'GET',
    nonRepeatable: true,
    validErrorStatusCodes: [404],
  },
  userToken: {
    url: HttpRequest.getUrlWithPrefix('/oauth/token', 'authPrefix'),
    urlRegex: /\/oauth\/token/,
    nonRepeatable: true,
    method: 'POST',
    critical: true,
  },
  checkToken: {
    url: HttpRequest.getUrlWithPrefix('/oauth/introspect', 'authPrefix'),
    urlRegex: /\/oauth\/introspect/,
    method: 'POST',
    critical: true,
  },
  institutions: {
    url: HttpRequest.getUrlWithPrefix('/public/institutions'),
    method: 'GET',
  },
  userRegister: {
    url: HttpRequest.getUrlWithPrefix('/register/user'),
    method: 'POST',
    nonRepeatable: true,
    dataStorage: 'RxMocks.UserRegister',
  },
  simpleUserRegister: {
    url: HttpRequest.getUrlWithPrefix('/register/user'),
    urlRegex: /\/register\/user/,
    method: 'POST',
    nonRepeatable: true,
    dataStorage: 'RxMocks.UserRegister',
  },
  userRegisterWithBulkId: {
    url: HttpRequest.getUrlWithPrefix('/public/bulk/register', 'authPrefix'),
    method: 'POST',
    nonRepeatable: true,
  },
  userRegisterConfirmationFromEmailLink: {
    url: HttpRequest.getUrlWithPrefix('/public/register/user/{regId}', 'authPrefix'),
    method: 'GET',
    nonRepeatable: true,
  },
  validateBulkId: {
    url: HttpRequest.getUrlWithPrefix('/public/bulk/validate'),
    urlRegex: /\/public\/bulk\/validate/,
    method: 'POST',
    critical: true,
  },
  userLogout: {
    url: HttpRequest.getUrlWithPrefix('/disconnect'),
    urlRegex: /\/disconnect/,
    method: 'GET',
    dataStorage: 'RxMocks.UserLogout',
  },
  userInfo: {
    url: HttpRequest.getUrlWithPrefix('/user'),
    urlRegex: /\/user/,

    method: 'GET',
    critical: true,
  },
  userProfile: {
    url: HttpRequest.getUrlWithPrefix('/userprofile'),
    urlRegex: /\/userprofile/,
    method: 'GET',
    dataStorage: 'RxMocks.UserInfo',
    critical: true,
  },
  userProfileExtended: {
    url: HttpRequest.getUrlWithPrefix('/userprofile/extended'),
    urlRegex: /\/userprofile\/extended/,
    method: 'GET',
    dataStorage: 'RxMocks.UserInfo',
    critical: true,
  },
  userProfilePreregister: {
    url: HttpRequest.getUrlWithPrefix('/userprofile/preregister'),
    urlRegex: /\/userprofile\/preregister/,
    method: 'GET',
    critical: true,
  },
  // Public endpoint - for register-finalize
  publicCombinedInfo: {
    url: HttpRequest.getUrlWithPrefix('/public/combinedinfo'),
    urlRegex: /\/public\/combinedinfo/,
    method: 'GET',
    dataStorage: 'RxMocks.UserInfo',
    critical: true,
  },
  // Authenticated endpoint - for user profile settings
  userCombinedInfo: {
    url: HttpRequest.getUrlWithPrefix('/user/combinedinfo'),
    urlRegex: /\/public\/combinedinfo/,
    method: 'GET',
    dataStorage: 'RxMocks.UserInfo',
    critical: true,
  },
  salutation: {
    url: HttpRequest.getUrlWithPrefix('/public/salutations'),
    method: 'GET',
    dataStorage: 'RxMocks.UserInfo',
  },
  changePreferences: {
    url: HttpRequest.getUrlWithPrefix('/userprofile/preferences'),
    nonRepeatable: true,
    method: 'POST',
  },
  resetPreferencesStructureEditor: {
    url: HttpRequest.getUrlWithPrefix('/userprofile/preferences/structure-editor'),
    nonRepeatable: true,
    method: 'DELETE',
  },
  clientInfo: {
    url: HttpRequest.getUrlWithPrefix('/public/clientinfo', 'authPrefix'),
    method: 'GET',
  },
  FactSubstanceDetails: {
    url: HttpRequest.getUrlWithPrefix('/results/sub/{item}/{fact}'),
    urlRegex: /results\/sub\/\d+\/[a-z]+/,
    method: 'POST',
  },
  DruglikenessSubstanceContent: {
    url: HttpRequest.getUrlWithPrefix(
      '/results/sub/{item}/calc?&databaseId={databaseId}&order={order}&resultName={resultName}&sort={sort}'
    ),
    urlRegex: /results\/sub\/\d+\/calc\?databaseId=\d+&order=[a-z]+&resultName=\w+&sort=[\w.]+/,
    method: 'GET',
  },
  DruglikenessSubstanceDetails: {
    url: HttpRequest.getUrlWithPrefix('/results/sub/xrn/calc?databaseId={databaseId}&xrn={xrn}'),
    urlRegex: /results\/sub\/xrn\/calc\?databaseId=\d+&xrn=\w+/,
    method: 'GET',
    dataStorage: 'RxMocks.DruglikenessSubstanceDetails',
  },
  StructureBySubstance: {
    url: HttpRequest.getUrlWithPrefix('/results/sub/mol?databaseId={databaseId}&xrn={xrn}'),
    urlRegex: /results\/sub\/molImage\?databaseId=\d+&xrn=\w+/,
    method: 'GET',
    dataStorage: 'RxMocks.StructureBySubstance',
  },
  BioSubstanceCategories: {
    url: HttpRequest.getUrlWithPrefix('/results/sub/{type}?&databaseId={databaseId}&xrn={xrn}&resultName={resultName}'),
    urlRegex: /results\/sub\?type=\w+\?databaseId=\d+&xrn=\w+&resultName=\w+/,
    method: 'GET',
  },
  BioSubstancePair: {
    url: HttpRequest.getUrlWithPrefix('/results/sub/{type}/pair?&databaseId={databaseId}&index={index}&resultName={resultName}'),
    urlRegex: /results\/sub\?type=\w+&pair=\w+/,
    method: 'GET',
  },
  BioSubstanceContent: {
    url: HttpRequest.getUrlWithPrefix('/results/bio/'),
    urlRegex: /results\/bio/,
    method: 'POST',
  },
  MarkushStructures: {
    url: HttpRequest.getUrlWithPrefix('/link/sub/markush?id={id}&databaseId={databaseId}&count={count}'),
    urlRegex: /link\/sub\/markush\/\?id=\d+&databaseId=\d+&count=\d/,
    method: 'GET',
    dataStorage: 'RxMocks.MarkushStructures',
  },
  allRelatedSubstances: {
    url: HttpRequest.getUrlWithPrefix('/link/sub/markush/all?databaseId={databaseId}&id={id}'),
    urlRegex: /link\/sub\/markush\/all\/\?databaseId=\d+id=\d+/,
    method: 'GET',
  },
  resultMoreReactionDetails: {
    url: HttpRequest.getUrlWithPrefix('/results/reac/{index}/details'),
    urlRegex: /\/results\/reac\/([A-Za-z0-9]+)\/details/,
    method: 'POST',
    dataStorage: 'RxMocks.ResultsMoreReactionDetails',
  },
  resultsTargetDetails: {
    url: HttpRequest.getUrlWithPrefix('/results/targets/details'),
    method: 'POST',
  },
  filterTaxonomies: {
    url: HttpRequest.getUrlWithPrefix(
      '/properties?context={context}&resultName={resultName}&databaseId={databaseId}&hitSetKey={hitSetKey}'
    ),
    urlRegex: /\/properties/,
    method: 'GET',
  },
  autosuggestSearch: {
    url: HttpRequest.getUrlWithPrefix('/suggest?search={query}&topValue=15', 'autosuggest'),
    urlRegex: /\?\$search=.*/,
    method: 'GET',
  },
  searchBuilder: {
    url: HttpRequest.getUrlWithPrefix('/constraints'),
    urlRegex: /\/constraints/,
    method: 'GET',
    critical: true,
  },
  boundOperators: {
    url: HttpRequest.getUrlWithPrefix('/apiinfo/bound_operators'),
    urlRegex: /\/apiinfo\/bound_operators/,
    method: 'GET',
    critical: true,
  },
  logicOperators: {
    url: HttpRequest.getUrlWithPrefix('/apiinfo/logic_operators'),
    urlRegex: /\/apiinfo\/logic_operators/,
    method: 'GET',
    critical: true,
  },
  forms: {
    url: HttpRequest.getUrlWithPrefix('/forms'),
    method: 'GET',
  },
  saveForm: {
    url: HttpRequest.getUrlWithPrefix('/form'),
    method: 'POST',
    nonRepeatable: true,
  },
  editForm: {
    url: HttpRequest.getUrlWithPrefix('/form'),
    method: 'PUT',
    nonRepeatable: true,
  },
  deleteForm: {
    url: HttpRequest.getUrlWithPrefix('/form/{formId}'),
    method: 'DELETE',
    nonRepeatable: true,
  },
  marvinLicense: {
    url: HttpRequest.getMarvinLicense('/rest-v1/license/grant'),
  },
  relativeResultsForCitations: {
    // Not required query params &sort={sort}&order={order}
    url: HttpRequest.getUrlWithPrefix(
      '/results/cit/{itemNumber}/?context={context}&resultName={resultName}&databaseId={databaseId}&firstItem={firstItem}&lastItem={lastItem}'
    ),
    method: 'GET',
  },
  titlesAbstractsForCitations: {
    url: HttpRequest.getUrlWithPrefix('/results/cit/ab?resultName={resultName}&databaseId={databaseId}&citationId={citationId}'),
    method: 'GET',
  },
  relativeResultsImage: {
    url: HttpRequest.getUrlWithPrefix('{imgPath}?access_token={accessToken}'),
  },
  relativePublicImage: {
    url: HttpRequest.getUrlWithPrefix('{imgPath}'),
  },
  suggestions: {
    url: HttpRequest.getUrlWithPrefix('/search/typeahead'),
    urlRegex: /\/search\/typeahead/,
    method: 'POST',
  },
  qletlookup: {
    url: HttpRequest.getUrlWithPrefix('/qletlookup'),
    urlRegex: /\/qletlookup/,
    method: 'POST',
  },
  relativeSmartContexts: {
    url: HttpRequest.getUrlWithPrefix('/results/smart/switch'),
    urlRegex: /\/results\/smart\/switch/,
    method: 'POST',
  },
  structureFromName: {
    url: HttpRequest.getUrlWithPrefix('/mol/fortext'),
    method: 'POST',
    nonRepeatable: true,
  },
  queryAsFile: {
    url: HttpRequest.getUrlWithPrefix('/export/query'),
    urlRegex: /\/export\/query/,
    method: 'POST',
    nonRepeatable: true,
  },
  heatmapCreateMatrix: {
    url: HttpRequest.getUrlWithPrefix('/heatmap'),
    urlRegex: /\/overview\/content/,
    method: 'GET',
    critical: true,
  },
  filterHeatmap: {
    url: HttpRequest.getUrlWithPrefix('/heatmap/filter'),
    nonRepeatable: true,
    method: 'POST',
  },
  cancelHeatmapRequests: {
    url: HttpRequest.getUrlWithPrefix('/heatmap/cancel?matrixName={matrixName}'),
    method: 'GET',
  },
  heatmapCell: {
    url: HttpRequest.getUrlWithPrefix('/heatmap/cell'),
    urlRegex: /\/heatmap\/cell/,
    method: 'POST',
    critical: true,
  },
  heatmapSort: {
    url: HttpRequest.getUrlWithPrefix('/heatmap/sort'),
    urlRegex: /\/heatmap\/sort/,
    method: 'POST',
    critical: true,
  },
  structureOptions: {
    url: HttpRequest.getUrlWithPrefix('/structure/constraint?context={context}'),
    urlRegex: /\/structure\/constraint\?context=/,
    method: 'GET',
    critical: true,
  },
  noImage: {
    url: HttpRequest.getUrlWithPrefix('/public/noimg?label={label}'),
    urlRegex: /\/public\/noimg/,
    method: 'GET',
  },
  molImage: {
    url: HttpRequest.getUrlWithPrefix('/molimg/{type}/{mark}/{dbId}/{hitSet}/{molFileHash}'),
    urlRegex: /\/molimg/,
    method: 'GET',
  },
  molRenderRequest: {
    url: HttpRequest.getUrlWithPrefix('/molimg_utils/render_request'),
    urlRegex: /\/molimg_utils\/render_request/,
    method: 'POST',
  },
  viewRelatedDocuments: {
    url: HttpRequest.getUrlWithPrefix('/link/sub/documents?&databaseId={databaseId}&id={id}'),
    urlRegex: /\/link\/sub\/documents/,
    method: 'GET',
    critical: true,
  },
  viewRelatedTargets: {
    url: HttpRequest.getUrlWithPrefix('/link/sub/targets?&databaseId={databaseId}&id={id}'),
    urlRegex: /\/link\/sub\/targets/,
    method: 'GET',
    critical: true,
  },
  viewRelatedPreparations: {
    url: HttpRequest.getUrlWithPrefix('/link/sub/preparations?&databaseId={databaseId}&id={id}'),
    urlRegex: /\/link\/sub\/preparations/,
    method: 'GET',
    critical: true,
  },
  viewRelatedReactions: {
    url: HttpRequest.getUrlWithPrefix('/link/sub/reactions?&databaseId={databaseId}&id={id}'),
    urlRegex: /\/link\/sub\/reactions/,
    method: 'GET',
    critical: true,
  },
  viewSubstancesById: {
    url: HttpRequest.getUrlWithPrefix('/search/substance/{id}?databaseId={databaseId}'),
    urlRegex: /\/search\/substance\//,
    method: 'GET',
    critical: true,
  },
  viewSubstancesForTargets: {
    url: HttpRequest.getUrlWithPrefix(
      '/link/target/sub?&databaseId={databaseId}&index={index}&hitSetKey={hitSetKey}&resultName={resultName}'
    ),
    urlRegex: /\/link\/target\/sub\?/,
    method: 'GET',
    critical: true,
  },
  viewDocumentsForTargets: {
    url: HttpRequest.getUrlWithPrefix(
      '/link/target/documents?&databaseId={databaseId}&index={index}&hitSetKey={hitSetKey}&resultName={resultName}'
    ),
    urlRegex: /\/link\/target\/documents\?/,
    method: 'GET',
    critical: true,
  },
  exportOutput: {
    url: HttpRequest.getUrlWithPrefix('/output?databaseId={databaseId}'),
    urlRegex: /\/output\?/,
    method: 'GET',
    critical: true,
  },
  exportOutputFormats: {
    url: HttpRequest.getUrlWithPrefix('/output/formats?databaseId={databaseId}&context={context}&main={main}'),
    urlRegex: /\/output\/formats\?/,
    method: 'GET',
    critical: true,
  },
  exportSpOutputOptions: {
    url: HttpRequest.getUrlWithPrefix('/output/planner'),
    urlRegex: /\/output\/planner/,
    method: 'POST',
    dataStorage: 'RxMocks.exportSpOutputOptions',
    critical: true,
  },
  getOutputItems: {
    url: HttpRequest.getUrlWithPrefix('/output/items?resultName={resultName}&databaseId={databaseId}&context={context}'),
    urlRegex: /\/output\/items\?/,
    method: 'GET',
    critical: true,
  },
  plannerMenuItems: {
    url: HttpRequest.getUrlWithPrefix('/planner/menu/items'),
    urlRegex: /\/planner\/menu\/items/,
    method: 'GET',
    dataStorage: 'RxMocks.plannerMenuItems',
    critical: true,
  },
  plannerNewHitsetBySimilar: {
    url: HttpRequest.getUrlWithPrefix('/planner/find/similar'),
    urlRegex: /planner\/find\/r\/similar/,
    method: 'POST',
    critical: true,
  },
  plannerNewHitsetByStructure: {
    url: HttpRequest.getUrlWithPrefix('/planner/new/structure'),
    urlRegex: /planner\/new\/r\/structure/,
    method: 'POST',
    critical: true,
  },
  retroSynthesisAvailableOptions: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/planner/options?r={random}'),
    urlRegex: /\/retrosynthesis\/planner\/options/,
    method: 'GET',
    critical: true,
  },
  vendorsInfo: {
    url: HttpRequest.getUrlWithPrefix('/v2/vendorsinfo?partners={partners}'),
    urlRegex: /\/v2\/vendorsinfo/,
    method: 'GET',
  },
  resetPreferencesAutoplanner: {
    url: HttpRequest.getUrlWithPrefix('/planner/auto/options'),
    urlRegex: /\/planner\/auto\/options/,
    method: 'DELETE',
    critical: true,
  },
  resetPreferencesRetrosynthesisPlanner: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/planner/options'),
    urlRegex: /\/retrosynthesis\/planner\/options/,
    method: 'DELETE',
    critical: true,
  },
  resetPreferencesConditionsAndExamples: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/conditions/options'),
    urlRegex: /\/retrosynthesis\/conditions\/options/,
    method: 'DELETE',
    critical: true,
  },
  resetSupplierPreferences: {
    url: HttpRequest.getUrlWithPrefix('/userprofile/preferences/expand'),
    urlRegex: /\/userprofile\/preferences\/expand/,
    method: 'DELETE',
    critical: true,
  },
  getPlannerReactionsConditions: {
    url: HttpRequest.getUrlWithPrefix('/planner/projects/{projectId}/routes/{routeId}/steps/{step}/reactions/conditions'),
    method: 'POST',
    dataStorage: 'RxMocks.getPlannerReactionsConditions',
  },
  conditionNavigatorPredict: {
    url: HttpRequest.getUrlWithPrefix(`${CONDITION_NAVIGATOR_API_URL}/predict`),
    method: 'POST',
  },
  conditionNavigatorProjects: {
    url: HttpRequest.getUrlWithPrefix(`${CONDITION_NAVIGATOR_API_URL}/projects?page={page}&size={size}&sort={sort}`),
    method: 'GET',
  },
  conditionNavigatorProject: {
    url: HttpRequest.getUrlWithPrefix(`${CONDITION_NAVIGATOR_API_URL}/projects/{id}`),
    method: 'GET',
    validErrorStatusCodes: [404],
  },
  conditionNavigatorProjectRank: {
    url: HttpRequest.getUrlWithPrefix(`${CONDITION_NAVIGATOR_API_URL}/projects/{projectId}/rank/{rankId}`),
    method: 'GET',
    validErrorStatusCodes: [404],
  },
  conditionNavigatorUpdateProject: {
    url: HttpRequest.getUrlWithPrefix(`${CONDITION_NAVIGATOR_API_URL}/projects/{id}`),
    method: 'PUT',
    validErrorStatusCodes: [404],
  },
  conditionNavigatorDeleteProject: {
    url: HttpRequest.getUrlWithPrefix(`${CONDITION_NAVIGATOR_API_URL}/projects/{id}`),
    method: 'DELETE',
    validErrorStatusCodes: [404],
  },
  getHistory: {
    url: HttpRequest.getUrlWithPrefix('/history/all'),
    method: 'GET',
    nonRepeatable: true,
  },
  getRecentHistory: {
    url: HttpRequest.getUrlWithPrefix('/recent-history?page={page}&size={size}'),
    method: 'GET',
    nonRepeatable: true,
  },
  saveHistory: {
    url: HttpRequest.getUrlWithPrefix('/history'),
    method: 'POST',
    nonRepeatable: true,
    dataStorage: 'RxMocks.saveHistory',
  },
  getHistoryTypes: {
    url: HttpRequest.getUrlWithPrefix('/history/types'),
    method: 'GET',
    dataStorage: 'RxMocks.getHistoryTypes',
  },
  modifyHistory: {
    url: HttpRequest.getUrlWithPrefix('/history'),
    nonRepeatable: true,
    method: 'PUT',
    dataStorage: 'RxMocks.modifyHistory',
  },
  removeHistory: {
    url: HttpRequest.getUrlWithPrefix('/history?resultName={resultName}&databaseId={databaseId}'),
    nonRepeatable: true,
    method: 'DELETE',
    dataStorage: 'RxMocks.removeHistory',
  },

  deleteSavedHistory: {
    url: HttpRequest.getUrlWithPrefix('/history/delete'),
    nonRepeatable: true,
    method: 'POST',
  },

  deleteRecentHistory: {
    url: HttpRequest.getUrlWithPrefix('/recent-history'),
    nonRepeatable: true,
    method: 'DELETE',
    dataStorage: 'RxMocks.removeHistory',
  },

  fetchRecentHistory: {
    url: HttpRequest.getUrlWithPrefix('/recent-history?page={page}&size={size}&sort={sort}'),
    nonRepeatable: true,
    method: 'GET',
  },

  searchRecentHistory: {
    url: HttpRequest.getUrlWithPrefix('/recent-history/search?page={page}&size={size}&sort={sort}&query={query}'),
    nonRepeatable: true,
    method: 'GET',
  },

  saveRecentHistory: {
    url: HttpRequest.getUrlWithPrefix('/recent-history'),
    method: 'POST',
  },

  importWhereClause: {
    url: HttpRequest.getUrlWithPrefix('/import/whereclause'),
    urlRegex: /\/import\/whereclause/,
    method: 'POST',
    critical: true,
  },
  restoreHistory: {
    url: HttpRequest.getUrlWithPrefix('/restore/results'),
    urlRegex: /\/restore\/results/,
    method: 'POST',
    dataStorage: 'RxMocks.restoreHistory',
    critical: true,
  },
  heatmapDetails: {
    url: HttpRequest.getUrlWithPrefix('/heatmap/details'),
    urlRegex: /\/heatmap\/details/,
    method: 'POST',
    critical: true,
  },
  logEvent: {
    url: HttpRequest.getUrlWithPrefix('/log/{event}'),
    method: 'POST',
  },
  hopInto: {
    // Query string is built by the caller — the inbound hopinto link carries an open-ended param set.
    url: HttpRequest.getUrlWithPrefix('/hopinto'),
    method: 'GET',
    nonRepeatable: true,
  },
  searchLabels: {
    url: HttpRequest.getUrlWithPrefix('/search/examples'),
    method: 'GET',
  },
  sortProperties: {
    url: HttpRequest.getUrlWithPrefix('/sort/properties'),
    method: 'POST',
  },
  molFormGroupConstraints: {
    url: HttpRequest.getUrlWithPrefix('/molecularformula/groupconstraints'),
    method: 'GET',
  },
  molFormPeriodicTable: {
    url: HttpRequest.getUrlWithPrefix('/molecularformula/periodictable'),
    method: 'GET',
  },
  aeMocksStat: {
    url: HttpRequest.getUrlWithPrefix('/public/devtools/aemocksstat'),
    method: 'GET',
  },
  healthCheck: {
    url: HttpRequest.getUrlWithPrefix('/healthcheck?forwardedChecks=true'),
    method: 'GET',
    nonRepeatable: true,
  },
  xmlAndAeHealthCheck: {
    url: HttpRequest.getUrlWithPrefix('/public/healthcheck/xmlae?forwardedChecks=true'),
    method: 'GET',
    nonRepeatable: true,
  },
  printDownload: {
    url: HttpRequest.getUrlWithPrefix('/print/download?access_token={access_token}&jobId={jobId}&document={documentName}'),
    urlRegex: /\/print\/download\?/,
    method: 'GET',
    critical: true,
  },
  ChemQuoteDownload: {
    url: HttpRequest.getUrlWithPrefix('/chemquote/download?access_token={access_token}&jobId={jobId}&document={documentName}'),
    urlRegex: /\/chemquote\/download\?/,
    method: 'GET',
    critical: true,
  },
  buildInfo: {
    url: HttpRequest.getUrlWithPrefix('/public/buildinfo'),
    method: 'GET',
  },
  taxonomy: {
    url: HttpRequest.getUrlWithPrefix('/taxonomy'),
    method: 'POST',
    nonRepeatable: true,
  },
  searchQueryFile: {
    url: HttpRequest.getUrlWithPrefix('/import/query'),
    urlRegex: /\/import\/query/,
    method: 'POST',
    critical: true,
  },
  searchBatchFile: {
    url: HttpRequest.getUrlWithPrefix('/import/batchfile?queryId={queryId}&remoteuser={remoteuser}&origin={origin}'),
    urlRegex: /\/import\/batchfile\?/,
    method: 'POST',
    critical: true,
  },
  linkPreparations: {
    url: HttpRequest.getUrlWithPrefix('/link/preparations'),
    urlRegex: /\/link\/preparations\?/,
    method: 'POST',
    critical: true,
  },
  pollingRequestSearchAdvanced: {
    url: HttpRequest.getUrlWithPrefix('/polling-request/search/advanced'),
    urlRegex: /\/polling-request\/search\/advanced/,
    method: 'POST',
    critical: true,
  },
  pollingRequestResults: {
    url: HttpRequest.getUrlWithPrefix('/polling-request/results'),
    urlRegex: /\/polling-request\/results/,
    method: 'POST',
    critical: true,
  },
  pollingRequestCoHitsets: {
    url: HttpRequest.getUrlWithPrefix('/polling-request/results/smart/switch'),
    urlRegex: /\/polling-request\/results\/smart\/switch/,
    method: 'POST',
  },
  pollingRequestFiltersSearch: {
    url: HttpRequest.getUrlWithPrefix('/polling-request/search/filter'),
    urlRegex: /\/polling-request\/search\/filter/,
    method: 'POST',
  },
  pollingRequestFiltersLimit: {
    url: HttpRequest.getUrlWithPrefix('/polling-request/search/limit'),
    urlRegex: /\/polling-request\/search\/limit/,
    method: 'POST',
  },
  pollingRequestFiltersGroup: {
    url: HttpRequest.getUrlWithPrefix('/polling-request/filters/group'),
    urlRegex: /\/polling-request\/filters\/group/,
    method: 'POST',
  },

  // New alerts
  getAllAlertsV2: {
    url: HttpRequest.getUrlWithPrefix('/useralerts/all'),
    method: 'GET',
  },
  getAlertInfoV2: {
    url: HttpRequest.getUrlWithPrefix('/useralerts/info?context={context}'),
    method: 'GET',
  },
  removeAlertV2: {
    url: HttpRequest.getUrlWithPrefix('/useralerts?uuid={uuid}'),
    method: 'DELETE',
  },
  createAlertV2: {
    url: HttpRequest.getUrlWithPrefix('/useralerts'),
    method: 'POST',
  },
  modifyAlertV2: {
    url: HttpRequest.getUrlWithPrefix('/useralerts'),
    method: 'PUT',
  },

  // New alerts hopinto
  hopintoUserAlert: {
    url: HttpRequest.getUrlWithPrefix('/useralert?alert={alert}'),
    method: 'GET',
  },
  hopintoUserAlertHitDetails: {
    url: HttpRequest.getUrlWithPrefix('/useralerthitdetails?alert={alert}&context={context}&query={query}'),
    method: 'GET',
  },

  // ID+
  idPlusAuth: {
    url: HttpRequest.getUrlWithPrefix('/idp_init'),
  },
  idPlusDetails: {
    url: HttpRequest.getUrlWithPrefix('/idp_feedback?details={details}'),
    method: 'GET',
  },

  remoteAccessEligibility: {
    url: HttpRequest.getUrlWithPrefix('/public/selfmanra/eligibility?email={email}'),
    method: 'GET',
  },

  remoteAccessActivate: {
    url: HttpRequest.getUrlWithPrefix('/public/selfmanra/activate'),
    method: 'POST',
  },

  rsProjectsList: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects?page={page}&size={size}&sort={sort}&r={random}'),
    method: 'GET',
  },
  rsProjectsChildList: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/child?childIds={childIds}&parentIds={parentIds}'),
    method: 'GET',
  },
  rsProjectsCount: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/count'),
    method: 'GET',
  },
  rsProjectRoutesProcess: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/{id}/routes/process'),
    method: 'GET',
  },
  rsAddProject: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/add'),
    method: 'POST',
  },
  rsEditProject: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/'),
    method: 'PUT',
  },
  rsDeleteProject: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/{id}'),
    method: 'DELETE',
  },
  rsProjectsBulkDelete: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/delete'),
    method: 'POST',
  },
  rsRetryProjectPredictedJob: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/{id}/rtjob/resubmit'),
    method: 'POST',
  },
  rsRetryProjectPublishedJob: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/{id}/published/resubmit'),
    method: 'POST',
  },
  rsFindSimilar: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/find/similar/{id}'),
    method: 'POST',
  },
  rsProjectRoutes: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/{projectId}/routes?page={page}&size={size}&sort={sort}&ids={ids}'),
    method: 'GET',
  },
  rsProjectInfo: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/{projectId}/info'),
    method: 'GET',
  },
  rsProjectRoutesLightweight: {
    url: HttpRequest.getUrlWithPrefix(
      '/retrosynthesis/projects/{projectId}/routes/basic?routeId={routeId}&page={page}&size={size}&sort={sort}&ids={ids}'
    ),
    method: 'GET',
  },
  rsProjectStructure: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/{projectId}/target'),
    method: 'GET',
  },
  rsProjectRoute: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/{projectId}/routes/{routeId}'),
    method: 'GET',
    validErrorStatusCodes: [403],
  },
  rsMultiStepPreview: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/multistep/preview'),
    method: 'POST',
  },
  rsMultiStepAddProject: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/multistep'),
    method: 'POST',
  },
  rsSingleStepAddProject: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/reaction'),
    method: 'POST',
  },
  rsProjectsPublished: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/published?page={page}&size={size}'),
    method: 'POST',
  },
  rsProjectsPredicted: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/predicted?page={page}&size={size}'),
    method: 'POST',
  },
  rsProjectsPredictedCancel: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/predicted/cancel?projectId={projectId}'),
    method: 'POST',
  },
  rsProjectsAddRouteSteps: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/{projectId}/routes/{routeId}/steps/add'),
    method: 'POST',
  },
  rsProjectsBulkDeleteRoutes: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/{projectId}/routes/delete'),
    method: 'POST',
  },
  rsProjectsDeleteRouteSteps: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/{projectId}/routes/{routeId}/steps/{step}'),
    method: 'DELETE',
  },
  rsProjectsGetRouteStepMollFile: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/{projectId}/routes/{routeId}/steps/{step}'),
    method: 'GET',
  },
  rsProjectsExportRoute: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/projects/{projectId}/routes/{routeId}/export'),
    method: 'GET',
  },
  rsProjectsSaveStepSelections: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/view/selection'),
    method: 'POST',
  },
  rsProjectsImportValidate: {
    url: HttpRequest.getUrlWithPrefix('/import/structures/validate'),
    method: 'POST',
  },
  rsProjectsImport: {
    url: HttpRequest.getUrlWithPrefix('/import/structures'),
    method: 'POST',
  },

  // CAS API
  casSearch: {
    url: HttpRequest.getUrlWithPrefix('/cas/search'),
    method: 'POST',
  },

  convertMolToSmiles: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/structure/transform/smiles'),
    method: 'POST',
  },

  validateSmiles: {
    url: HttpRequest.getUrlWithPrefix('/retrosynthesis/structure/validate/smiles'),
    method: 'POST',
  },

  emailExists: {
    url: HttpRequest.getUrlWithPrefix('/public/email?email={email}'),
    method: 'GET',
    nonRepeatable: true,
    validErrorStatusCodes: [404],
  },
  createIntegratedFeedback: {
    url: HttpRequest.getUrlWithPrefix('/search/feedback'),
    method: 'POST',
  },
  aiSearchGetSummary: {
    url: HttpRequest.getUrlWithPrefix(
      '/api/semantic/summary?resultName={resultName}&semanticResultName={semanticResultName}&databaseId={databaseId}&query={query}'
    ),
    method: 'GET',
    fullResponseWithStatusCode: true,
  },
  // Target V1 contract (RX-24506 SD-4 / RX-24693): POST body + summaryId polling.
  // The two entries above stay for BE compatibility during migration only.
  aiSearchSummary: {
    url: HttpRequest.getUrlWithPrefix('/api/semantic/summary'),
    method: 'POST',
    fullResponseWithStatusCode: true,
    validErrorStatusCodes: [400, 404],
  },
  aiSearchSummaryStatusById: {
    url: HttpRequest.getUrlWithPrefix('/api/semantic/summary/status?summaryId={summaryId}'),
    method: 'GET',
    fullResponseWithStatusCode: true,
    validErrorStatusCodes: [404],
  },
  aiSearchLimitToReferencedDocuments: {
    url: HttpRequest.getUrlWithPrefix('/search/citation/summary/references'),
    method: 'POST',
  },
  aiSearchLimitToReferencedReactions: {
    url: HttpRequest.getUrlWithPrefix('/search/reaction/summary/references'),
    method: 'POST',
  },
};

export default RequestUrls;
